import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AccountCreditTransaction, AccountCreditTransactionType } from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, formatCurrency } from '../../components/design/AtomFitUI.js';
import { adminApi } from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';

const creditTypeLabels: Record<AccountCreditTransactionType, string> = {
  REFUND_CANCELLATION: translate(
    'pages.admin.admin_credits_page.credit_type_labels.refund_cancellation',
  ),
  MANUAL_COMPENSATION: translate(
    'pages.admin.admin_credits_page.credit_type_labels.manual_compensation',
  ),
  REFERRAL_BONUS: translate('pages.admin.admin_credits_page.credit_type_labels.referral'),
  MEMBERSHIP_TOP_UP: translate(
    'pages.admin.admin_credits_page.credit_type_labels.membership_top_up',
  ),
  MEMBERSHIP_EXPIRY: translate(
    'pages.admin.admin_credits_page.credit_type_labels.membership_expiry',
  ),
  BOOKING_USAGE: translate('pages.admin.admin_credits_page.credit_type_labels.booking_usage'),
};

const creditTypes: AccountCreditTransactionType[] = [
  'REFUND_CANCELLATION',
  'MANUAL_COMPENSATION',
  'REFERRAL_BONUS',
  'MEMBERSHIP_TOP_UP',
  'MEMBERSHIP_EXPIRY',
  'BOOKING_USAGE',
];

function formatDate(value: string | Date) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function accountLabel(account?: AccountCreditTransaction['account'] | null) {
  if (!account) return '-';
  return [account.firstName, account.lastName].filter(Boolean).join(' ') || account.email;
}

export function AdminCreditsPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [transactions, setTransactions] = useState<AccountCreditTransaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [type, setType] = useState<AccountCreditTransactionType | ''>('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isPlatform) return;
    setLoading(true);
    try {
      const res = await adminApi.listCredits({
        page,
        ...(query ? { q: query } : {}),
        ...(type ? { type } : {}),
      });
      setTransactions(res.data.data);
      setTotalPages(res.data.meta.totalPages);
    } finally {
      setLoading(false);
    }
  }, [isPlatform, page, query, type]);

  useEffect(() => {
    void load();
  }, [load]);

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(queryInput.trim());
  }

  return (
    <AdminLayout
      title={translate(
        'pages.admin.admin_credits_page.admin_credits_page.admin_layout.credit_edit',
      )}
      eyebrow={translate('pages.admin.admin_credits_page.admin_credits_page.admin_layout.eyebrow')}
    >
      {!isPlatform ? (
        <div className="af-card" style={{ padding: 24, color: 'var(--af-muted)' }}>
          {translate('pages.admin.admin_credits_page.admin_credits_page.div_card.credit_edit')}
        </div>
      ) : (
        <>
          <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
            <form
              onSubmit={applySearch}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1fr) 240px auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <label className="af-label">
                {translate('pages.admin.admin_credits_page.admin_credits_page.label_label.search')}

                <input
                  className="af-input"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                  placeholder={translate(
                    'pages.admin.admin_credits_page.admin_credits_page.input_input.placeholder_email_name',
                  )}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_credits_page.admin_credits_page.label_label.type')}

                <CustomSelect
                  value={type}
                  onChange={(value) => {
                    setPage(1);
                    setType(value as AccountCreditTransactionType | '');
                  }}
                  options={[
                    {
                      value: '',
                      label: translate(
                        'pages.admin.admin_credits_page.admin_credits_page.custom_select.options',
                      ),
                    },
                    ...creditTypes.map((item) => ({ value: item, label: creditTypeLabels[item] })),
                  ]}
                />
              </label>
              <button className="af-outline-btn" style={{ padding: '12px 16px' }} type="submit">
                {translate(
                  'pages.admin.admin_credits_page.admin_credits_page.button_outline_btn.filter',
                )}
              </button>
            </form>
          </div>

          <div className="af-card" style={{ overflow: 'auto' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--af-border)' }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>
                {translate('pages.admin.admin_credits_page.admin_credits_page.div.credit_edit')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--af-muted)', marginTop: 4 }}>
                {translate('pages.admin.admin_credits_page.admin_credits_page.div.date')}
              </div>
            </div>
            <CreditTransactionsTable transactions={transactions} loading={loading} />
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '8px 16px' }}
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {translate(
                  'pages.admin.admin_credits_page.admin_credits_page.button_outline_btn.previous',
                )}
              </button>
              <span style={{ padding: '8px 12px', fontSize: 13, color: 'var(--af-muted)' }}>
                {page} {'/'} {totalPages}
              </span>
              <button
                className="af-outline-btn"
                style={{ padding: '8px 16px' }}
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {translate(
                  'pages.admin.admin_credits_page.admin_credits_page.button_outline_btn.next',
                )}
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function CreditTransactionsTable({
  transactions,
  loading,
}: {
  transactions: AccountCreditTransaction[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 18,
          color: 'var(--af-muted)',
        }}
      >
        <Spinner />{' '}
        {translate(
          'pages.admin.admin_credits_page.credit_transactions_table.div.loading_credit_edit',
        )}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
        {translate('pages.admin.admin_credits_page.credit_transactions_table.div.credit_edit')}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
          {[
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.copy'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.scope'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.type'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.copy_2'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.reason'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.date'),
            translate('pages.admin.admin_credits_page.credit_transactions_table.tr.copy_3'),
          ].map((heading) => (
            <th
              key={heading}
              style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 10,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--af-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transactions.map((transaction, index) => (
          <tr
            key={transaction.id}
            style={{
              borderBottom: index < transactions.length - 1 ? '1px solid var(--af-border)' : 'none',
            }}
          >
            <td style={{ padding: '12px 16px' }}>{accountLabel(transaction.account)}</td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {transaction.tenantId
                ? transaction.tenantId.slice(0, 8)
                : translate('pages.admin.admin_credits_page.credit_transactions_table.td.copy')}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {creditTypeLabels[transaction.type]}
            </td>
            <td
              style={{
                padding: '12px 16px',
                fontWeight: 900,
                color:
                  transaction.amountInSmallestUnit >= 0 ? 'var(--af-success)' : 'var(--af-danger)',
                whiteSpace: 'nowrap',
              }}
            >
              {transaction.amountInSmallestUnit >= 0 ? '+' : ''}
              {formatCurrency(transaction.amountInSmallestUnit, transaction.currency)}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)', maxWidth: 280 }}>
              {transaction.reason}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {formatDate(transaction.createdAt)}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              {accountLabel(transaction.performedByAccount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
